import { X } from 'lucide-react'
import type { ReactNode, Ref } from 'react'
import { createPortal } from 'react-dom'
import { Button } from './Button'
import { useDocSlot } from './PageAction'
import { Tooltip } from './Tooltip'
import './doc-slot.css'

/**
 * What an entry in the slot is, which is also where it stands in it.
 *
 * Who is here leads, because it is about the people rather than the document;
 * the document's own details follow, then the way through a document that
 * plays; a notice about the document comes last.
 * The order is decided here once, so no screen positions an entry and two
 * entries cannot trade places depending on which mounted first.
 */
export type DocSlotKind = 'together' | 'details' | 'playback' | 'notice'

const ORDER: Record<DocSlotKind, number> = {
  together: 0,
  details: 1,
  playback: 2,
  notice: 3,
}

/** An entry a person put up, and so can put away: its own close, at its end. */
type Closable = { onClose?: () => void; closeLabel?: string }

/**
 * One entry's box, for whatever draws into the slot: a screen through
 * `DocSlot`, or the shell, whose roster fills its entry through a ref.
 *
 * `row` is one line, as tall as a rail cell. `tall` is for an entry whose
 * content is read rather than glanced at, which gets air inside its box.
 */
export function DocSlotEntry({
  kind,
  size = 'row',
  ref,
  onClose,
  closeLabel = 'Close',
  children,
}: {
  kind: DocSlotKind
  size?: 'row' | 'tall'
  ref?: Ref<HTMLDivElement>
  children?: ReactNode
} & Closable) {
  return (
    <div
      className="docslot__entry"
      data-kind={kind}
      data-size={size}
      style={{ order: ORDER[kind] }}
    >
      <div ref={ref} className="docslot__body">
        {children}
      </div>
      {onClose ? (
        <Tooltip label={closeLabel}>
          <Button
            variant="quiet"
            icon
            aria-label={closeLabel}
            onClick={onClose}
          >
            <X size={16} aria-hidden />
          </Button>
        </Tooltip>
      ) : null}
    </div>
  )
}

/**
 * One entry in the slot above an open document.
 *
 * Everything said ABOUT the document while it owns the screen — who is reading
 * along, its details, why it will not print as designed — is one kind of
 * thing, so it takes one place and one look: a box on the rails' surface and
 * hairline, centered over the sheet, level with the rails' top edge. A screen
 * declares an entry and the shell draws where it goes.
 *
 * Where no document owns the screen there is no slot, and the entry stands in
 * place, in the flow of the page that declared it.
 */
export function DocSlot({
  kind,
  size,
  onClose,
  closeLabel,
  children,
}: {
  kind: DocSlotKind
  size?: 'row' | 'tall'
  children: ReactNode
} & Closable) {
  const slot = useDocSlot()
  if (!slot) return <>{children}</>
  return createPortal(
    <DocSlotEntry
      kind={kind}
      onClose={onClose}
      closeLabel={closeLabel}
      {...(size ? { size } : {})}
    >
      {children}
    </DocSlotEntry>,
    slot,
  )
}
